import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, ArrowRight } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, withAlpha, STATUS_COLORS } from "../lib/bookingStatus";
import { deNumber } from "../lib/numberFormat";

const fmtEUR = (n) => `${deNumber(Math.round(n || 0), 0)} €`;
const monthLabel = (yyyymm) => {
  const [y, m] = (yyyymm || "").split("-").map((x) => parseInt(x, 10));
  if (!y || !m) return yyyymm;
  const names = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  return `${names[m - 1]} ${String(y).slice(2)}`;
};

/**
 * RevenueChart — kompakte Cashflow-Übersicht der letzten 6 Monate.
 * Feb 2026 · Balken pro Monat: gold (offen · noch nicht fällig), rot
 * (überfällig), navy (bezahlt) — proportional zum jeweiligen Anteil des
 * Gesamt-Umsatzes im Zeitraum. Storno-Gutschriften ziehen ihren negativen
 * Betrag von den Beträgen des Ausstellungs-Monats ab (das Backend zählt
 * cancelled-Ursprungsrechnungen aus, damit keine Doppelbuchung entsteht).
 *
 * Klick auf einen Balken filtert die Rechnungs-Liste per Query-Param nach
 * genau diesem Monat — Umsatz-Zoom-Feature aus der Feb 2026 · Roadmap.
 */
export default function RevenueChart() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/invoices/summary/monthly?months=6");
        setData(data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
        setData({ months: [] });
      }
    })();
  }, []);

  if (data === null) {
    return (
      <div className="bg-white border border-navy/10 p-6 md:p-8 mt-10" data-testid="team-revenue-loading">
        <div className="label-caps">Umsatz-Auswertung wird geladen …</div>
      </div>
    );
  }
  if (!data.months?.length) return null;

  // Maximum bar height reference — largest total in the window (Vorjahres-
  // Referenzen fließen mit ein, damit hohe YoY-Linien nicht über die Balken
  // hinausragen).
  const maxTotal = Math.max(1, ...data.months.map((m) => Math.max(m.total || 0, m.prior_year_total || 0)));
  const totals = data.months.reduce(
    (acc, m) => ({
      open: acc.open + (m.open || 0),
      overdue: acc.overdue + (m.overdue || 0),
      paid: acc.paid + (m.paid || 0),
    }),
    { open: 0, overdue: 0, paid: 0 },
  );
  const grand = totals.open + totals.overdue + totals.paid;

  return (
    <section className="bg-white border border-navy/10 p-6 md:p-8 mt-10" data-testid="team-revenue-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}>
            <TrendingUp className="w-5 h-5 text-navy" strokeWidth={1.6} />
          </div>
          <div>
            <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Umsatz-Auswertung</div>
            <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight">Cashflow · letzte 6 Monate</h2>
            <p className="text-[12.5px] text-navy-dark/60 mt-1 max-w-2xl">
              Rechnungen nach Ausstellungs-Monat. Storno-Gutschriften fließen mit ihren negativen Beträgen mit ein.
            </p>
          </div>
        </div>
        <Link
          to="/team/invoices"
          data-testid="revenue-chart-open-invoices"
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-1.5 self-start"
        >
          Zum Archiv <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Bars */}
      <div className="flex items-end gap-3 md:gap-5 min-h-[180px]" data-testid="team-revenue-bars">
        {data.months.map((m) => {
          const total = m.total || 0;
          const heightPct = total > 0 ? Math.max(6, (total / maxTotal) * 100) : 3;
          const openPct    = total > 0 ? (m.open    / total) * 100 : 0;
          const overduePct = total > 0 ? (m.overdue / total) * 100 : 0;
          const paidPct    = total > 0 ? (m.paid    / total) * 100 : 0;
          const clickable = total > 0 || m.count > 0;
          return (
            <button
              key={m.label}
              type="button"
              onClick={() => clickable && nav(`/team/invoices?month=${m.label}`)}
              disabled={!clickable}
              className={`flex-1 flex flex-col items-center min-w-0 group ${clickable ? "cursor-pointer" : "cursor-default opacity-60"}`}
              data-testid={`revenue-bar-${m.label}`}
              title={clickable ? `${m.count} Rechnung${m.count === 1 ? "" : "en"} — klicken zum Filtern` : "Keine Rechnungen"}
            >
              <div
                className="w-full text-center text-[11px] font-medium text-navy-dark/80 mb-1 truncate"
                data-testid={`revenue-bar-total-${m.label}`}
              >
                {total > 0 ? fmtEUR(total) : "—"}
              </div>
              {/* Feb 2026 · Bar-Wrapper mit VOLLER Achsen-Höhe. Der farbige
                  Balken sitzt bottom-aligned und wächst nur auf `heightPct%`;
                  die Vorjahres-Linie ist ein Geschwister-Element und wird an
                  `(prior/maxTotal)*100 %` der gesamten Achse verankert — so
                  bleibt die YoY-Aussage konsistent, egal ob Vorjahr höher
                  oder niedriger als der laufende Monat war. */}
              <div
                className="w-full max-w-[64px] relative"
                style={{ height: 180 }}
              >
                <div
                  className={`absolute bottom-0 left-0 right-0 flex flex-col justify-end overflow-hidden border border-navy/10 transition-transform ${clickable ? "group-hover:scale-[1.03] group-hover:border-gold" : ""}`}
                  style={{ height: `${heightPct}%`, minHeight: 12 }}
                >
                  {paidPct > 0 && <div style={{ height: `${paidPct}%`, background: BRAND_COLORS.navy }} />}
                  {overduePct > 0 && <div style={{ height: `${overduePct}%`, background: STATUS_COLORS.red }} />}
                  {openPct > 0 && <div style={{ height: `${openPct}%`, background: BRAND_COLORS.gold }} />}
                </div>
                {/* Vorjahres-Referenzlinie (YoY) — dünne graue gestrichelte
                    Linie auf Höhe des Vorjahresmonats-Werts, referenziert
                    die volle Chart-Achse (maxTotal enthält Vorjahr + aktuelle
                    Monate, damit nichts über den Rand ragt). */}
                {(m.prior_year_total || 0) > 0 && (
                  <div
                    className="absolute left-[-3px] right-[-3px] border-t-2 border-dashed pointer-events-none"
                    style={{
                      bottom: `${Math.max(0, Math.min(100, ((m.prior_year_total || 0) / maxTotal) * 100))}%`,
                      borderColor: "rgba(90, 100, 120, 0.65)",
                    }}
                    data-testid={`revenue-bar-prior-${m.label}`}
                    title={`Vorjahresmonat: ${fmtEUR(m.prior_year_total)}`}
                  />
                )}
              </div>
              <div className={`mt-2 text-[10.5px] tracking-[0.12em] uppercase whitespace-nowrap ${clickable ? "text-navy-dark/60 group-hover:text-gold" : "text-navy-dark/40"}`}>
                {monthLabel(m.label)}
              </div>
              <div className="text-[10px] text-navy-dark/40">
                {m.count} R.
              </div>
            </button>
          );
        })}
      </div>

      {/* Legende + Totals */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="team-revenue-legend">
        <div className="flex items-center gap-2 text-[12px] text-navy-dark">
          <span className="w-3 h-3 rounded-sm" style={{ background: BRAND_COLORS.gold }} />
          <span>Offen</span>
          <span className="ml-auto font-medium">{fmtEUR(totals.open)}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-navy-dark">
          <span className="w-3 h-3 rounded-sm" style={{ background: STATUS_COLORS.red }} />
          <span>Überfällig</span>
          <span className="ml-auto font-medium">{fmtEUR(totals.overdue)}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-navy-dark">
          <span className="w-3 h-3 rounded-sm" style={{ background: BRAND_COLORS.navy }} />
          <span>Bezahlt</span>
          <span className="ml-auto font-medium">{fmtEUR(totals.paid)}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-navy-dark/70">
          <span className="w-6 border-t-2 border-dashed shrink-0" style={{ borderColor: "rgba(90,100,120,0.55)" }} />
          <span>Vorjahr</span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-navy/10 flex items-center justify-between text-[13px] text-navy">
        <span className="label-caps">Summe (6 Monate)</span>
        <span className="font-display text-xl" data-testid="team-revenue-grand-total">{fmtEUR(grand)}</span>
      </div>
    </section>
  );
}
